import {
    FetchState,
    DebitCardManagementState,
    DebitCardListResponse,
    CardProviderOption,
    CardProvider,
    CardProviderOptionsResponse,
    DebitCardManagerModalViewState,
    DebitCardRecord,
    SetCardPinRequest,
    OrderReplacementCardRequest,
    CardStatus,
    PotentialCardHolder,
    DependentRequest,
    CardAddDependentResponse,
    Address
} from "@/shared/types";
import * as CardService from "@/shared/api/cardService";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import _ from "lodash";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useUserStore } from "@/shared/store/user";
import { useNotifierStore } from "@/shared/store/notifier";
import { useContactInfoStore } from "@/shared/store/contactinfo";
import * as datadog from "@/shared/datadog";

function defaultNoneProviderOptions (): CardProviderOption {
    return {
        cardProvider: CardProvider.None,
        capability: {
            canManageCard: false,
            canReplaceCard: false,
            canDeactivateCard: false,
            canOrderNewCards: false,
            canInitializePin: false,
            canUpdatePin: false,
            canConfirmCardReplacement: false,
            canOrderCardWhenAddingAuthSigner: false
        }
    };
}

export const initialState: DebitCardManagementState = {
    debitCardsFetchState: FetchState.Uninitialized,
    debitCards: [],
    potentialCardHolders: [],
    primaryCard: null,
    cardProviderOptionsFetchState: FetchState.Uninitialized,
    cardProviderOptions: null,
    focusedDebitCard: null,
    modalViewState: DebitCardManagerModalViewState.None,
    deactivationReason: null,
    pendingNewCardHolder: null
};

export const useDebitCardManagementStore = defineStore("debitCardManagement", {
    state: (): DebitCardManagementState => (_.cloneDeep(initialState)),

    getters: {
        isLoaded: state => state.debitCardsFetchState === FetchState.Loaded &&
            state.cardProviderOptionsFetchState === FetchState.Loaded,

        debitCardsLoading: state => state.debitCardsFetchState === FetchState.Loading,

        debitCardsLoaded: state => state.debitCardsFetchState === FetchState.Loaded,

        debitCardsFailed: state => state.debitCardsFetchState === FetchState.Failed,

        cardProviderOptionsOrDefault: state => state.cardProviderOptions
            ? state.cardProviderOptions
            : defaultNoneProviderOptions(),

        isModalOpen: state => state.modalViewState !== DebitCardManagerModalViewState.None,

        hasPrimaryCard (): null|boolean {
            return this.isLoaded ? this.debitCards.length > 0 : null;
        },

        primaryDebitCard (): null|DebitCardRecord {
            return this.hasPrimaryCard ? this.debitCards[0] : null;
        },

        potentailNewCardHolderName: state => {
            if (state.pendingNewCardHolder) {
                return `${state.pendingNewCardHolder.firstName} ${state.pendingNewCardHolder.lastName}`;
            }

            const user = useUserStore().user;
            return `${user.firstName} ${user.lastName}`;
        },

        mailingAddress (): null|Address {
            const contactInfoStore = useContactInfoStore();
            if (!contactInfoStore.contactInformation
                || !contactInfoStore.contactInformation.mailingAddress) {
                const data = {
                    cardNumberLast4: this.focusedDebitCard.cardNumberLast4,
                    userDate: Date.now()
                };

                datadog.log(`MailingAddress not found`, data, "error");
                return null;
            }

            return contactInfoStore.contactInformation.mailingAddress;
        }
    },

    actions: {
        closeModal () {
            this.focusedDebitCard = null;
            this.modalViewState = DebitCardManagerModalViewState.None;
        },
        setFocusedDebitCard (debitCard: DebitCardRecord|PotentialCardHolder) {
            this.focusedDebitCard = debitCard;
            this.modalViewState = DebitCardManagerModalViewState.ManageCardForm;
        },
        setModalState (viewState: DebitCardManagerModalViewState) {
            this.modalViewState = viewState;
        },
        setCardPinIsSet (arg: { cardId: number, isPinSet: boolean}) {
            const card = this.debitCards.find(c => c.cardId === arg.cardId);
            if (card) {
                card.isPinSet = arg.isPinSet;
            }
        },
        setCardStatus (arg: { cardId: number, cardStatus: CardStatus }) {
            const card = this.debitCards.find(c => c.cardId === arg.cardId);
            if (card) {
                card.cardStatus = arg.cardStatus;
            }
        },

        setPotentialNewCardHolder (newPotentialCardHolder: null|PotentialCardHolder) {
             this.pendingNewCardHolder = newPotentialCardHolder;
        },

        resetDebitCards () {
            this.debitCards = [];
            this.debitCardsFetchState = FetchState.Uninitialized;
        },

        resetCardProviderOptions () {
            this.cardProviderOptions = null;
            this.cardProviderOptionsFetchState = FetchState.Uninitialized;
        },

        setDebitCardsFetchState (fetchState: FetchState) {
            this.debitCardsFetchState = fetchState;
        },

        setDebitCardList (response: DebitCardListResponse) {
            /* Wex-provided cards currently come in without a cardId field, but the UI really needs one.
             * If every card comes in without an ID, then we can assume the card-provider ignores that field
             * And so we can assign an arbitrary one just for the UI to use
             */
            if (response.cards.every(c => !c.cardId)) {
                response.cards.forEach((c, index) => { c.cardId = index; });
            }

            this.debitCards = response.cards;
            this.potentialCardHolders = response.potentialCardHolders || [];
            this.primaryCard = response.primaryCard;
            this.debitCardsFetchState = FetchState.Loaded;
        },

        setCardProviderOptionsFetchState (fetchState: FetchState) {
            this.cardProviderOptionsFetchState = fetchState;
        },

        setCardProviderOptions (response: CardProviderOptionsResponse) {
            this.cardProviderOptions = {
                cardProvider: response.cardProvider,
                capability: response.capability
            };

            this.cardProviderOptionsFetchState = FetchState.Loaded;
        },

        addDependentCardResponseToCard (newDependentCard: CardAddDependentResponse) {
            const tempCard: DebitCardRecord = {
                ...newDependentCard,
                isPrimary: false,
                cardNumberLast4: "----",
                cardId: -1,
                cardStatus: CardStatus.Pending,
                authorizedSignerPendingId: null,
                userCanManageCard: false
            };

            this.debitCards.push(tempCard);
        },

        getDebitCards (args?: { username: string, partnerId: number }): Promise<any> {
            if (this.debitCardsFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setDebitCardsFetchState(FetchState.Loading);
            return CardService.getDebitCards(args)
                .then(response => {
                    this.setDebitCardList(response.data);
                }).catch(err => {
                    this.resetDebitCards();
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },

        getCardProviderOptions (): Promise<any> {
            if (this.cardProviderOptionsFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setCardProviderOptionsFetchState(FetchState.Loading);
            return CardService.getCardProviderOptions()
                .then(response => {
                    this.setCardProviderOptions(response.data);
                }).catch(err => {
                    this.resetCardProviderOptions();
                    return storeHelper.handleError({error: err, rethrow: true});
                });
        },

        loadAll (): Promise<any> {
            return Promise.allSettled([
                this.getDebitCards(),
                this.getCardProviderOptions()
            ]);
        },

        setFocusedCardPin (request: SetCardPinRequest): Promise<any> {
            if (!this.focusedDebitCard) { return Promise.resolve(); }

            const card = this.focusedDebitCard;
            return CardService.setDebitCardPin(card.cardId, request)
                .then(response => {
                    this.setCardPinIsSet({cardId: card.cardId, isPinSet: true});
                }).catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },

        orderPrimaryCard (): Promise<any> {
            if (this.primaryDebitCard) {
                // they already have a primary debit card and should be able to get here, ask them to refresh
                return Promise.reject("Please reload the page and try again.");
            }

            return CardService.orderPrimaryCard()
                .then(response => {
                    this.resetDebitCards();
                    return this.getDebitCards();
                })
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },

        orderReplacementDebitCard (request: OrderReplacementCardRequest): Promise<any> {
            if (!this.focusedDebitCard) { return Promise.resolve(); }

            const card = this.focusedDebitCard;
            return CardService.orderReplacementCard(card.cardId, request)
            .then(response => {
                this.setCardStatus({cardId: card.cardId, cardStatus: request.cardStatus });
            }).catch(err => {
                return storeHelper.handleError({ error: err, rethrow: true });
            });
          },

        orderDuplicatePrimaryDebitCard (): Promise<any> {
            if (! this.primaryDebitCard) {
                return Promise.reject("Primary debit card is not loaded, and so could not be re-ordered.");
            }

            const notifierStore = useNotifierStore();
            return CardService.orderPrimaryDuplicateCard()
                .then(response => {
                    notifierStore.setSuccess(
                        `Duplicate ordered for card ending in ${this.primaryDebitCard.cardNumberLast4}`);
                })
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },

        orderCardForDependent (request: DependentRequest): Promise<any> {
            const notifierStore = useNotifierStore();
            return CardService.addFiservDebitCardDependent(request)
                .then(response => {
                    this.addDependentCardResponseToCard(response.data);
                    notifierStore.setSuccess(
                        `Card request submitted for new card for ${request.firstName} ${request.lastName}.`
                    );
                }).catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        orderCardForPrimaryAndDependent (request: DependentRequest): Promise<any> {
            return this.orderPrimaryCard()
                .then(response => {
                    return this.orderCardForDependent(request);
                });
        }
    }
});
